/**
 * ConnectivityService — online/offline detection.
 *
 * Reachability = a bounded HEAD ping to /api/version. On native we do NOT trust
 * navigator.onLine or the DOM online/offline events — the Android 16 WebView
 * keeps navigator.onLine `true` with the radio off and never fires `offline`,
 * and @capacitor/network can fall back to that same lying web signal. So native
 * defaults to offline until a probe proves otherwise and polls continuously.
 */

import { apiFetch } from '@/lib/api'
import { isNative } from '@/lib/platform'
import { logConn } from '@/lib/connectivity-log'
import { Capacitor } from '@capacitor/core'
import { Network } from '@capacitor/network'

// Steady-state reachability poll. Runs in BOTH states (not just while offline):
// without a trustworthy native offline event, a mid-session drop is only caught
// by actively probing.
const POLL_INTERVAL_MS = 10_000
// Hard ceiling on a single probe. AbortSignal.timeout is not reliably honored
// by the native WebView fetch — an offline fetch can hang on the OS connect
// timeout (~30-60s), which is the "takes 30s to notice I'm offline" bug. So
// check() races the fetch against a manual timer and treats a timeout as offline.
// 8s (up from 4s): a weak construction-site uplink can legitimately need >4s for
// the HEAD round-trip, and the tighter bound was flipping genuinely-online users
// offline → "Start Analysis" wrongly queued (Milan, 2026-07-23). Paired with a
// one-shot retry in check() before we conclude offline.
const PROBE_TIMEOUT_MS = 8_000

export class ConnectivityService {
  private static instance: ConnectivityService
  private _isOnline = false
  // True once check() has actually settled at least once. Consumers that don't
  // want to render an "offline" state before we've confirmed it (e.g. the
  // offline banner) gate on this to avoid a flash on the pessimistic native seed.
  private _verifiedOnce = false
  private listeners = new Set<(online: boolean) => void>()
  // In-flight verification, shared by concurrent check() callers. Sharing the
  // PROMISE (not returning the cached bool) matters: a worst-case verification
  // (2 × 8s probe timeouts) outlasts the 10s poll interval, so an on-demand
  // check (e.g. pre-analyze) frequently lands while a poll's check is running —
  // returning the cached value there hands back exactly the stale state the
  // caller was trying to re-verify.
  private inFlight: Promise<boolean> | null = null
  private poll: ReturnType<typeof setInterval> | null = null

  private constructor() {
    if (typeof window === 'undefined') return

    // Pessimistic on native: assume offline until a probe confirms online. The
    // WebView's navigator.onLine lies (true with the radio off), so seeding from
    // it treats an offline user as online for the whole probe window — the exact
    // bug where Start Analysis fires online while offline. Defaulting offline is
    // the safe direction for an offline-first app: a genuinely-online user flips
    // to online within one probe RTT (<1s), an offline user is never wrongly
    // treated as online. Web's navigator.onLine is reliable, so seed from it.
    this._isOnline = isNative() ? false : navigator.onLine
    logConn('seed', isNative() ? 'native=false' : `web=${navigator.onLine}`)

    window.addEventListener('offline', () => {
      logConn('dom-event', 'offline')
      this.setOnline(false, 'dom-offline')
    })
    window.addEventListener('online', () => {
      logConn('dom-event', 'online')
      void this.check('dom-online')
    })

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.check('visibility')
    })

    // @capacitor/network, WHEN its native plugin is active, is an instant and
    // authoritative signal. When it falls back to the web shim it just mirrors
    // navigator.onLine, so we only trust `connected === false` as a fast offline
    // hint (never as proof of online — the probe confirms that). Harmless either
    // way; the poll below is the real backstop.
    if (isNative()) {
      // Whether the NATIVE side of @capacitor/network is actually registered in
      // this build — false means we're on the web shim (mirrors the lying
      // navigator.onLine) and instant detection is impossible: only the poll
      // catches drops. This one line answers "why was detection slow" in a dump.
      logConn('plugin', Capacitor.isPluginAvailable('Network') ? 'native' : 'web-shim')
      void Network.addListener('networkStatusChange', (status) => {
        logConn('native-event', `connected=${status.connected}`)
        if (!status.connected) this.setOnline(false, 'native-event')
        else void this.check('native-event')
      })
    }

    // Steady-state poll — always running, both directions. Catches mid-session
    // drops and recoveries without a reload and without a trustworthy DOM event.
    this.poll = setInterval(() => { void this.check('poll') }, POLL_INTERVAL_MS)

    // NOTE: no `this.check()` here — the initial check is queued from
    // getInstance() after the singleton reference is set to avoid re-entrant
    // construction (RangeError: Maximum call stack size exceeded).
  }

  static getInstance(): ConnectivityService {
    if (!ConnectivityService.instance) {
      ConnectivityService.instance = new ConnectivityService()
      // Defer the initial check until after the singleton reference is set,
      // so re-entrant getInstance() calls from inside fetch wrappers return
      // the existing instance instead of constructing a second one.
      if (typeof window !== 'undefined') {
        queueMicrotask(() => void ConnectivityService.instance.check('initial'))
      }
    }
    return ConnectivityService.instance
  }

  get isOnline(): boolean {
    return this._isOnline
  }

  get verifiedOnce(): boolean {
    return this._verifiedOnce
  }

  subscribe(callback: (online: boolean) => void): () => void {
    this.listeners.add(callback)
    callback(this._isOnline)
    return () => { this.listeners.delete(callback) }
  }

  // Concurrent callers join the in-flight verification and get its FRESH
  // result. `attempts` lets latency-sensitive callers opt out of the retry:
  // the pre-analyze re-probe uses 1 — a wrong "offline" there only queues the
  // snag (safe direction), while a second 8s timeout would keep the user
  // staring at a "checking connection" toast. Joining an in-flight check keeps
  // that check's own attempts budget.
  async check(trigger = 'manual', opts?: { attempts?: number }): Promise<boolean> {
    if (this.inFlight) return this.inFlight
    const run = this.verify(trigger, opts?.attempts ?? 2).finally(() => { this.inFlight = null })
    this.inFlight = run
    return run
  }

  private async verify(trigger: string, attempts: number): Promise<boolean> {
    const wasVerified = this._verifiedOnce
    const startedAt = Date.now()
    try {
      // Fast offline hints (never trusted as "online" — the probe confirms that).
      if (isNative()) {
        try {
          const status = await Network.getStatus()
          if (!status.connected) { this.setOnline(false, `${trigger} getStatus=false`); return false }
        } catch { /* plugin unavailable — fall through to the probe */ }
      } else if (!navigator.onLine) {
        this.setOnline(false, `${trigger} navigator=false`)
        return false
      }

      // Reachability probe, hard-bounded by a manual race: the native fetch may
      // ignore AbortSignal.timeout and hang on the OS connect timeout, so the
      // second promise guarantees each attempt resolves within PROBE_TIMEOUT_MS.
      // ONE retry before we conclude offline: a single slow/dropped probe on a
      // weak site connection shouldn't flip the whole app offline (which wrongly
      // queues "Start Analysis"). Only two consecutive misses = offline.
      for (let attempt = 1; attempt <= attempts; attempt++) {
        try {
          const res = await Promise.race([
            apiFetch(`/api/version?_=${Date.now()}`, {
              method: 'HEAD',
              cache: 'no-store',
              signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
            }),
            new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error('probe_timeout')), PROBE_TIMEOUT_MS)),
          ])
          if (res.ok) {
            this.setOnline(true, `${trigger} probe=${res.status} ${Date.now() - startedAt}ms${attempt > 1 ? ` a${attempt}` : ''}`)
            return true
          }
          // Reachable but non-OK (proxy/5xx): retry once, then treat as offline.
          if (attempt === attempts) {
            this.setOnline(false, `${trigger} probe=${res.status} ${Date.now() - startedAt}ms`)
            return false
          }
        } catch (err) {
          const detail = `${trigger} probe=fail(${err instanceof Error ? err.message : 'err'}) ${Date.now() - startedAt}ms a${attempt}`
          if (attempt === attempts) {
            // A failed probe while ALREADY offline doesn't flip state (setOnline
            // no-ops), but it's still evidence — log it so the dump shows the
            // poll was alive and what each probe attempt did.
            if (!this._isOnline) logConn('probe-error', detail)
            this.setOnline(false, detail)
            return false
          }
          // First miss — retry immediately. Logged UNCONDITIONALLY: the
          // interesting retry is the one that happens while still online (a
          // slow probe on a weak link — the Milan case), and that's exactly
          // the one an offline-gated log would drop.
          logConn('probe-retry', detail)
        }
      }
      return this._isOnline // unreachable: the loop always returns
    } finally {
      this._verifiedOnce = true
      // If setOnline didn't fire (probe landed on the same value we seeded),
      // still notify so subscribers gated on verifiedOnce can re-render.
      if (!wasVerified) this.listeners.forEach(cb => cb(this._isOnline))
    }
  }

  private setOnline(online: boolean, cause = 'unknown') {
    if (this._isOnline === online) return
    this._isOnline = online
    logConn('flip', `→${online ? 'online' : 'offline'} ${cause}`)
    this.listeners.forEach(cb => cb(online))
  }
}
